import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search,
  FilterList,
  Download,
  Refresh,
  MoreVert,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  Receipt,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { parseDate } from '../utils/dateUtils';
import { accountService } from '../services/api';
import { AccountTransaction, TransactionStatus } from '../types/account';

interface PartnerTransactionsProps {
  partnerId: string;
}

const PartnerTransactions: React.FC<PartnerTransactionsProps> = ({ partnerId }) => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<AccountTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<AccountTransaction | null>(null);

  // Statistics
  const [stats, setStats] = useState({
    totalTransactions: 0,
    totalVolume: 0,
    successRate: 0,
    averageTransaction: 0,
  });

  // Fetch transactions by partnerId. Not gated on the partner having accounts: agents have no
  // wallet accounts (their collections hit the shared agent pool), but their transactions still
  // carry the partner on the header, so the partnerId query returns them.
  useEffect(() => {
    fetchTransactions();
  }, [page, rowsPerPage, statusFilter]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params: any = {
        partnerId,
        page,
        size: rowsPerPage,
        status: statusFilter !== 'ALL' ? (statusFilter as TransactionStatus) : undefined,
        search: searchTerm || undefined,
      };

      const response = await accountService.getTransactions(params);
      setTransactions(response.content || []);
      setTotalCount(response.totalElements || 0);

      // Calculate statistics
      calculateStats(response.content || []);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (txns: AccountTransaction[]) => {
    const completed = txns.filter(t => t.status === TransactionStatus.COMPLETED);
    const totalVolume = completed.reduce((sum, t) => sum + t.totalAmount, 0);
    const successRate = txns.length > 0 ? (completed.length / txns.length) * 100 : 0;
    const averageTransaction = completed.length > 0 ? totalVolume / completed.length : 0;

    setStats({
      totalTransactions: txns.length,
      totalVolume,
      successRate,
      averageTransaction,
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = () => {
    setPage(0);
    fetchTransactions();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, transaction: AccountTransaction) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransaction(transaction);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTransaction(null);
  };

  const handleViewDetails = () => {
    if (selectedTransaction) {
      navigate(`/transactions/${selectedTransaction.id}`);
    }
    handleMenuClose();
  };

  const handleViewVoucher = () => {
    if (selectedTransaction?.voucherNumber) {
      navigate(`/voucher/${selectedTransaction.voucherNumber}`);
    }
    handleMenuClose();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle color="success" fontSize="small" />;
      case 'FAILED':
        return <Cancel color="error" fontSize="small" />;
      case 'PENDING':
        return <HourglassEmpty color="warning" fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'COMPLETED':
        return 'success';
      case 'FAILED':
        return 'error';
      case 'PENDING':
        return 'warning';
      case 'REVERSED':
        return 'default';
      default:
        return 'default';
    }
  };

  const exportTransactions = () => {
    const csvContent = [
      ['Date', 'Reference', 'Description', 'Status', 'Amount', 'Currency'],
      ...transactions.map(t => [
        format(parseDate(t.transactionDate), 'yyyy-MM-dd HH:mm'),
        t.transactionRef || t.transactionReference || '',
        t.description,
        t.status,
        t.totalAmount.toFixed(2),
        t.currency,
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${partnerId}-${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
  };

  return (
    <Box>
      {/* Statistics Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Transactions
                  </Typography>
                  <Typography variant="h5">
                    {stats.totalTransactions}
                  </Typography>
                </Box>
                <Receipt color="primary" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Total Volume
                  </Typography>
                  <Typography variant="h5">
                    ${stats.totalVolume.toLocaleString()}
                  </Typography>
                </Box>
                <AttachMoney color="success" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Success Rate
                  </Typography>
                  <Typography variant="h5">
                    {stats.successRate.toFixed(1)}%
                  </Typography>
                </Box>
                <TrendingUp color="info" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="caption">
                    Avg Transaction
                  </Typography>
                  <Typography variant="h5">
                    ${stats.averageTransaction.toFixed(2)}
                  </Typography>
                </Box>
                <TrendingDown color="warning" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by reference or voucher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="FAILED">Failed</MenuItem>
                <MenuItem value="REVERSED">Reversed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Search />}
              onClick={handleSearch}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Box display="flex" gap={1} justifyContent="flex-end">
              <Tooltip title="Refresh">
                <IconButton onClick={fetchTransactions}>
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export">
                <IconButton onClick={exportTransactions}>
                  <Download />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Transactions Table */}
      <TableContainer component={Paper}>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date/Time</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Voucher</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="center">Postings</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  {loading ? 'Loading...' : 'No transactions found'}
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {format(parseDate(transaction.transactionDate), 'MMM dd, yyyy')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {format(parseDate(transaction.transactionDate), 'HH:mm:ss')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {transaction.transactionRef || transaction.transactionReference}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {transaction.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {transaction.voucherNumber || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {getStatusIcon(transaction.status || transaction.transactionStatus)}
                      <Chip
                        label={transaction.status || transaction.transactionStatus || 'UNKNOWN'}
                        color={getStatusColor(transaction.status || transaction.transactionStatus || '')}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="medium">
                      {transaction.currency} {transaction.totalAmount.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {transaction.numberOfPostings || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, transaction)}
                    >
                      <MoreVert />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
        {selectedTransaction?.voucherNumber && (
          <MenuItem onClick={handleViewVoucher}>View Voucher</MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default PartnerTransactions;